/**
 * KuenstlerinPage
 * Hub for the four "Künstlerin" sub-pages (Über mich, Über meine Skulpturen,
 * Entstehung einer Skulptur, Medien & Artikel).
 */

import { Link } from 'react-router-dom'
import { useArtworks } from '@/hooks/useArtworks'
import { Seo, ANNA_PERSON_LD } from '@/components/Seo'
import { useT, STRINGS, useFieldLang } from '@/context/LanguageContext'
import './ContentPage.css'

export function KuenstlerinPage() {
  const { getSection, loading } = useArtworks()
  const section = getSection('kuenstlerin')
  const t = useT()
  const pick = useFieldLang()

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>{t(STRINGS.common.loading)}</p>
      </div>
    )
  }

  if (!section || !section.pages) {
    return (
      <div className="error-screen">
        <p>{t(STRINGS.common.notFound)}</p>
      </div>
    )
  }

  return (
    <main className="content-page">
      <Seo
        title="Künstlerin"
        description="Anna Sacher Santana — österreichische Bildhauerin und Kinder- und Jugendpsychiaterin. Über mich, künstlerische Position, Entstehungsprozess, Medien & Artikel."
        jsonLd={ANNA_PERSON_LD}
      />
      <div className="content-container">
        <header className="content-header">
          <h1>{t(STRINGS.nav.kuenstlerin)}</h1>
        </header>

        <div className="content-card-grid">
          {section.pages.map((page) => {
            // Hand-curated previews per slug — the auto-stripped markdown
            // turned into a messy blob once pages started embedding raw
            // HTML (portrait hero, image grids).
            const PREVIEW_OVERRIDES: Record<string, { de: string; en: string }> = {
              'ueber-mich': { de: 'Vita und Fotos', en: 'Vita and Photos' },
            }
            // Representative portrait per Künstlerin sub-page. Picks
            // semantically match: ivy hero → Über mich, Anna sculpting
            // → Über meine Skulpturen, Anna modelling large piece →
            // Entstehung, vernissage shot → Medien & Artikel.
            const CARD_IMAGES: Record<string, { id: number; alt: string }> = {
              'ueber-mich': { id: 36183, alt: 'Anna Sacher Santana vor grüner Efeu-Wand' },
              'ueber-meine-skulpturen-kunst-als-spiegel-der-seele': { id: 36173, alt: 'Anna Sacher Santana modelliert eine Skulptur' },
              'entstehung-einer-skulptur': { id: 36172, alt: 'Anna Sacher Santana bei der Arbeit an einer Großskulptur' },
              'medien-artikel': { id: 36170, alt: 'Anna Sacher Santana bei einer Vernissage' },
            }
            const overrideNode = PREVIEW_OVERRIDES[page.slug]
            const cardImage = CARD_IMAGES[page.slug]
            const localDesc = pick(page.description, page.description_en) || page.description
            const preview = overrideNode
              ? t(overrideNode)
              : localDesc
                  .replace(/<[^>]+>/g, ' ')
                  .replace(/^#+\s.*$/gm, '')
                  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                  .replace(/[*_>`]/g, '')
                  .replace(/\s+/g, ' ')
                  .trim()
                  .slice(0, 180)
            const truncated = !overrideNode && localDesc.length > 180
            return (
              <Link
                key={page.slug}
                to={`/kuenstlerin/${page.slug}`}
                className={`content-card ${cardImage ? 'content-card-with-image' : ''}`}
              >
                {cardImage && (
                  <div className="content-card-image">
                    <img
                      src={`https://api-storage.arkserver.arkturian.com/storage/media/${cardImage.id}`}
                      alt={cardImage.alt}
                      loading="lazy"
                    />
                  </div>
                )}
                <div className={cardImage ? 'content-card-body' : ''}>
                  <h2 className="content-card-title">{pick(page.title, page.title_en) || page.title}</h2>
                  {preview && (
                    <p className="content-card-preview">
                      {preview}
                      {truncated ? '…' : ''}
                    </p>
                  )}
                  <span className="content-card-link">{t(STRINGS.common.readMore)}</span>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </main>
  )
}
